import React, { useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
    FiMail,
    FiSend,
    FiX,
    FiCheck,
    FiUsers,
    FiSearch,
    FiEye,
} from "react-icons/fi";
import { Loader, Home, PanelsTopLeft } from "lucide-react";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";
import Toast from "../common/Toast";
import { uuidToShortId } from "../../lib/common/shortId";
import { supabase } from "../../config/supabase";
import {
    QUOTATION_DEPARTMENTS,
    getSentQuotationDepartments,
    sendQuotationEmail,
} from "../../lib/supabase/quotations";
import {
    isFinalPricingRequired,
    isDepartmentQuotable,
    computeEstimationTotals,
    computeEffectivePricing,
} from "../../lib/supabase/finalPricingModes";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEPARTMENT_ICONS = {
    roof: Home,
    wall: PanelsTopLeft,
    floor: HiOutlineSquare3Stack3D,
};

const SEND_PERMISSION = "send_quotation_page";

const hasExactPermission = (userProfile, permission) =>
    Array.isArray(userProfile?.permissions) &&
    userProfile.permissions.includes(permission);

const formatMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "—";

    return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 2,
    }).format(amount);
};

const addressToText = (address) => {
    if (!address) return "—";
    if (typeof address === "string") return address;

    return [
        address.street,
        address.street_address,
        address.address_line_1,
        address.suburb,
        address.city,
        address.state,
        address.postcode,
    ].filter(Boolean).join(", ") || "—";
};

const QuotationEmailSender = ({
    job,
    preselectDepartment,
    company,
    userProfile,
    onClose,
    onSent,
}) => {
    const canSend = hasExactPermission(userProfile, SEND_PERMISSION);

    const [userPickerOpen, setUserPickerOpen] = useState(false);
    const [sending, setSending] = useState(false);

    const [ccInput, setCcInput] = useState("");
    const [ccEmails, setCcEmails] = useState([]);
    const [users, setUsers] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [pickerSelectedIds, setPickerSelectedIds] = useState([]);

    const [previewOpen, setPreviewOpen] = useState(false);
    const defaultCcAppliedRef = useRef(false);

    const estimationTotals = useMemo(
        () => computeEstimationTotals(job?.pricing_entries),
        [job?.pricing_entries]
    );
    const effectivePricing = useMemo(
        () => computeEffectivePricing({ finalPricings: job?.final_pricings, estimationTotals }),
        [job?.final_pricings, estimationTotals]
    );
    const requireFinalPricing = isFinalPricingRequired(company);
    const [lineItemDisplay, setLineItemDisplay] = useState("itemized"); // 'itemized' | 'rolled_up'

    // A department counts as "priced" here (shown in the Priced Departments
    // panel, selectable to include in the email) only when it is quotable
    // under the company's final_pricing_required_for_quotation setting — the
    // exact same isDepartmentQuotable test quotations.js uses for row
    // matching. For a company that requires a manual final price, a
    // department carrying only the estimator's quoting fallback must not be
    // offered here (and therefore can't be pre-selected and emailed to a
    // customer as if it were a reviewed number).
    const pricedDepartments = useMemo(() => (
        QUOTATION_DEPARTMENTS
            .filter(department => isDepartmentQuotable(effectivePricing.departments[department.key], requireFinalPricing))
            .map(department => ({
                key: department.key,
                label: department.label,
                pricing: {
                    final_price: effectivePricing.departments[department.key].amount,
                    notes: effectivePricing.departments[department.key].notes,
                },
                source: effectivePricing.departments[department.key].source,
            }))
    ), [effectivePricing, requireFinalPricing]);

    const [selectedDepartmentKeys, setSelectedDepartmentKeys] = useState(() => (
        preselectDepartment
            ? [preselectDepartment]
            : pricedDepartments.map(department => department.key)
    ));
    const [sentDepartmentKeys, setSentDepartmentKeys] = useState(new Set());
    const [noteOverrides, setNoteOverrides] = useState({});

    const [form, setForm] = useState({
        to: job?.customer_mail || job?.customer_email || "",
        subject: `Quotation - ${job?.unique_id || ""} - ${addressToText(job?.address)}`,
        content: `Hi ${job?.customer_name || ""},\n\nPlease find your quotation below for job:\n${job?.unique_id || ""} - ${addressToText(job?.address)}`,
    });

    const defaultCcUserIds = useMemo(() => {
        const settings = company?.settings || {};

        return (
            settings.quotation_default_cc_user_ids ||
            settings.default_cc_user_ids ||
            []
        );
    }, [company?.settings]);

    const isJobCancelled = !!job?.is_cancelled;

    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = event => {
            if (event.key === "Escape" && !sending) {
                onClose?.();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose, sending]);

    useEffect(() => {
        if ((!userPickerOpen && defaultCcUserIds.length === 0) || !company?.id) return;

        supabase
            .from("users")
            .select("id, full_name, username, email, role, avatar_url")
            .eq("company_id", company.id)
            .order("full_name", { ascending: true })
            .then(({ data, error }) => {
                if (error) {
                    console.error("Error loading users:", error);
                    Toast.error("Failed to load users");
                    return;
                }

                setUsers((data || []).filter(u => !!u.email));
            });
    }, [userPickerOpen, company?.id, defaultCcUserIds]);

    useEffect(() => {
        if (defaultCcAppliedRef.current) return;
        if (!users.length || !defaultCcUserIds.length) return;

        const defaultEmails = users
            .filter(user => defaultCcUserIds.includes(user.id))
            .map(user => user.email)
            .filter(Boolean);

        setCcEmails(defaultEmails.map(email => email.toLowerCase()));

        defaultCcAppliedRef.current = true;
    }, [users, defaultCcUserIds]);

    useEffect(() => {
        if (!job?.id || !company?.id) return undefined;

        let active = true;

        const loadSent = async () => {
            try {
                const sent = await getSentQuotationDepartments(company.id, job.id);
                if (active) setSentDepartmentKeys(sent);
            } catch (error) {
                console.error("Failed to load sent quotation departments:", error);
            }
        };

        loadSent();

        return () => {
            active = false;
        };
    }, [job?.id, company?.id]);

    const jobShortId = useMemo(() => {
        try {
            return uuidToShortId(job?.id);
        } catch {
            return job?.short_id || job?.id;
        }
    }, [job?.id, job?.short_id]);

    const contactUrl = `https://app.notifyapp.com.au/contact/${jobShortId}`;

    const logoUrl = useMemo(() => {
        const isEnterprise = company?.subscription_plan === "enterprise";
        return isEnterprise && company?.enterprise_logo_url
            ? company.enterprise_logo_url
            : "https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/wide-logo-black.svg";
    }, [company?.subscription_plan, company?.enterprise_logo_url]);

    const selectedDepartments = useMemo(() => (
        pricedDepartments.filter(department => selectedDepartmentKeys.includes(department.key))
    ), [pricedDepartments, selectedDepartmentKeys]);

    const getDepartmentNotes = (department) => (
        noteOverrides[department.key] ?? department.pricing?.notes ?? ""
    );

    const setDepartmentNotes = (departmentKey, value) => {
        setNoteOverrides(prev => ({ ...prev, [departmentKey]: value }));
    };

    const allDepartmentKeys = useMemo(() => (
        pricedDepartments.map(department => department.key)
    ), [pricedDepartments]);

    // Produces the rows for both the visible "Email Content" pricing table
    // and buildEmailHtml's HTML table below — the single source of truth for
    // "what pricing does this email show" so the two can never drift apart.
    // Sourced from selectedDepartments (not pricedDepartments) so the table
    // only ever shows the departments the checkboxes actually selected.
    const getPricingTableRows = () => {
        // A department with a manual final price always renders as a single
        // row, regardless of the itemized/rolled-up toggle below — there's no
        // field-level breakdown to itemize once a flat override exists.
        const finalRows = selectedDepartments
            .filter(department => department.source === "final")
            .map(department => ({
                key: department.key,
                departmentKey: department.key,
                label: department.label,
                amount: department.pricing.final_price,
                notes: getDepartmentNotes(department),
                editable: true,
            }));

        const fallbackDepartments = selectedDepartments.filter(department => department.source !== "final");

        if (fallbackDepartments.length === 0) return finalRows;

        if (lineItemDisplay === "rolled_up") {
            return [
                ...finalRows,
                ...fallbackDepartments.map(department => ({
                    key: department.key,
                    departmentKey: department.key,
                    label: department.label,
                    amount: department.pricing.final_price,
                    notes: getDepartmentNotes(department),
                    editable: true,
                })),
            ];
        }

        const fallbackKeys = new Set(fallbackDepartments.map(department => department.key));

        return [
            ...finalRows,
            ...effectivePricing.lines
                .filter(line => fallbackKeys.has(line.department))
                .map(line => ({
                    key: line.id,
                    departmentKey: line.department,
                    label: `${line.label}${line.unit_number ? ` (Unit ${line.unit_number})` : ""}`,
                    amount: line.value,
                    notes: "",
                    editable: false,
                })),
        ];
    };

    // Column-1 header text for the pricing table, kept in sync between the
    // visible "Email Content" table and buildEmailHtml so they never drift.
    const getPricingTableHeaderLabel = () => (
        lineItemDisplay === "itemized" ? "Line Item" : "Department"
    );

    if (!canSend) return null;

    const allDepartmentsSelected =
        allDepartmentKeys.length > 0 &&
        selectedDepartmentKeys.length === allDepartmentKeys.length;

    const toggleDepartment = (key) => {
        setSelectedDepartmentKeys(prev =>
            prev.includes(key)
                ? prev.filter(item => item !== key)
                : [...prev, key]
        );
    };

    const selectAllDepartments = () => {
        setSelectedDepartmentKeys(allDepartmentKeys);
    };

    const clearAllDepartments = () => {
        setSelectedDepartmentKeys([]);
    };

    const unselectSentDepartments = () => {
        setSelectedDepartmentKeys(prev =>
            prev.filter(key => !sentDepartmentKeys.has(key))
        );
    };

    const addCcEmail = (email) => {
        const clean = String(email || "").trim().replace(/,$/, "").toLowerCase();
        if (!clean) return;

        if (!EMAIL_RE.test(clean)) {
            Toast.error(`Invalid email: ${clean}`);
            return;
        }

        setCcEmails(prev => {
            if (prev.includes(clean)) return prev;
            return [...prev, clean];
        });
    };

    const removeCcEmail = (email) => {
        setCcEmails(prev => prev.filter(item => item !== email));
    };

    const handleCcKeyDown = (e) => {
        if (["Enter", "Tab", ","].includes(e.key)) {
            e.preventDefault();
            addCcEmail(ccInput);
            setCcInput("");
        }

        if (e.key === "Backspace" && !ccInput && ccEmails.length > 0) {
            setCcEmails(prev => prev.slice(0, -1));
        }
    };

    const toggleUserPicker = (userId) => {
        setPickerSelectedIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const applySelectedUsersToCc = () => {
        users
            .filter(user => pickerSelectedIds.includes(user.id))
            .forEach(user => addCcEmail(user.email));

        setUserPickerOpen(false);
    };

    const filteredUsers = users.filter(user => {
        const q = userSearch.toLowerCase();
        return (
            user.full_name?.toLowerCase().includes(q) ||
            user.username?.toLowerCase().includes(q) ||
            user.email?.toLowerCase().includes(q) ||
            user.role?.toLowerCase().includes(q)
        );
    });

    const buildEmailHtml = () => {
        const rawContent = form.content || "";

        const signatureMatch = rawContent.match(/\n\nKind regards,[\s\S]*/i);

        const mainContent = signatureMatch
            ? rawContent.replace(signatureMatch[0], "")
            : rawContent;

        const safeMainContent = mainContent
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\n/g, "<br />");

        const pricingTableRows = getPricingTableRows();

        const departmentsHtml = pricingTableRows.map(row => `
            <tr>
                <td style="padding:12px;border-bottom:1px solid #eef0f3;font-size:13px;color:#111827;font-weight:700;font-family:'Poppins',monospace;text-transform:capitalize;">
                    ${row.label}
                </td>
                <td style="padding:12px;border-bottom:1px solid #eef0f3;font-size:13px;color:#111827;font-weight:700;font-family:'Courier New',monospace;font-variant-numeric:tabular-nums;white-space:nowrap;">
                    ${formatMoney(row.amount)}
                </td>
                <td style="padding:12px;border-bottom:1px solid #eef0f3;font-size:13px;color:#4b5563;">
                    ${row.notes || "-"}
                </td>
            </tr>
        `).join("");

        return `
        <html>
        <head>
            <meta name="color-scheme" content="light dark">
            <meta name="supported-color-schemes" content="light dark">
            <style>
                :root {
                    color-scheme: light only;
                    supported-color-schemes: light only;
                }

                body {
                    margin: 0 !important;
                    padding: 0 !important;
                    background: #f4f5f7 !important;
                    color: #111827 !important;
                }
            </style>
        </head>

        <body style="margin:0;padding:0;background:#f4f5f7 !important;color:#111827 !important;">
            <div style="margin:0;padding:0;background:#f4f5f7 !important;font-family:Poppins,Helvetica,sans-serif;color:#111827 !important;">
                <div style="max-width:680px;margin:0 auto;padding:28px 16px;">
                    <div style="background:#ffffff !important;color:#111827 !important;border-radius:18px;overflow:hidden;border:1px solid #e5e7eb;">
                        <div style="padding:26px 28px;text-align:center;border-bottom:1px solid #eef0f3;background:#ffffff !important;">
                            <img
                                src="https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/wide-logo-black.svg"
                                alt="Notify"
                                width="160"
                                style="display:block;height:auto;max-height:80px;max-width:180px;width:auto;object-fit:contain;margin:0 auto;border:0;"
                            />
                            <div style="text-align:center;font-size:14px;font-weight:700;color:#111827 !important;margin-top:10px;font-family:Poppins,Helvetica,sans-serif;">
                                Notify <span style="font-weight:500;color:#27272a;letter-spacing:0.4px;">- BUILT AROUND YOUR BUSINESS</span>
                            </div>
                        </div>

                        <div style="padding:30px 28px;background:#ffffff !important;color:#111827 !important;">
                            <p style="font-size:15px;line-height:1.7;margin:0 0 22px;color:#374151;">
                                ${safeMainContent}
                            </p>

                            <table style="width:100%;border-collapse:collapse;background:#ffffff;border:1px solid #eef0f3;border-radius:12px;overflow:hidden;">
                                <thead>
                                    <tr style="background:#f3f4f6;">
                                        <th style="padding:12px;text-align:left;font-size:11px;color:#6b7280;letter-spacing:0.3px;">${(getPricingTableHeaderLabel() || "Item").toUpperCase()}</th>
                                        <th style="padding:12px;text-align:left;font-size:11px;color:#6b7280;letter-spacing:0.3px;">FINAL PRICE</th>
                                        <th style="padding:12px;text-align:left;font-size:11px;color:#6b7280;letter-spacing:0.3px;">NOTES</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${departmentsHtml || `
                                        <tr>
                                            <td colspan="3" style="padding:14px;font-size:13px;color:#667085;">
                                                No departments selected.
                                            </td>
                                        </tr>
                                    `}
                                </tbody>
                            </table>

                            <div style="margin:30px 0 8px;padding:20px;background:#f9fafb;border:1px solid #eef0f3;border-radius:16px;text-align:left;">
                                <p style="margin:0 0 6px;font-size:15px;font-weight:700;color:#111827;">
                                    Need to share files or send us a message?
                                </p>
                                <p style="margin:0;font-size:13px;line-height:1.6;color:#667085;white-space:pre-line;">You can reply directly to this email with updates, questions, or important job details including file attachments such as plans, photos, or documents.\n\nOur team will receive everything instantly.</p>
                            </div>

                            <div style="margin-top:4rem;padding-top:18px;border-top:1px solid #eef0f3;">
                                <p style="font-size:14px;line-height:1.8;margin:0;color:#374151;">
                                    Kind regards,
                                </p>
                            </div>

                            <div style="margin-top:1rem;padding-top:18px;">
                                <p style="font-size:14px;font-weight:600;line-height:1.8;margin:0;color:#374151;">
                                    ${userProfile?.full_name || userProfile?.username}
                                </p>
                                ${userProfile?.position ? `
                                <p style="font-size:14px;line-height:1.8;margin:0;color:#374151;">
                                    ${userProfile.position}
                                </p>` : ``}
                                <p style="font-size:14px;line-height:1.8;margin:0;color:#374151;margin-top:2rem;">
                                    <span style="font-weight:500;">T:</span> ${company?.phone} | <span style="font-weight:500;">E:</span> ${userProfile?.email}
                                </p>
                            </div>
                        </div>

                        <div style="padding:26px 28px;background:#f9fafb;border-top:1px solid #eef0f3;text-align:left;">
                            <div style="margin:0 0 14px;">
                                <p style="margin:0 0 8px;font-size:13px;color:#4b5563;line-height:1.6;">
                                    📧
                                    <a href="mailto:info@notifyapp.com.au" style="color:#111827;text-decoration:none;font-weight:600;">
                                        info@notifyapp.com.au
                                    </a>
                                </p>
                                <p style="margin:0;font-size:13px;color:#4b5563;line-height:1.6;">
                                    🌐
                                    <a href="https://notifyapp.com.au" style="color:#111827;text-decoration:none;font-weight:600;">
                                        notifyapp.com.au
                                    </a>
                                </p>
                            </div>

                            <div style="height:1px;background:#dfe3e8;margin:16px 0;"></div>

                            <p style="margin:0;font-size:11px;color:#6b7280;line-height:1.6;">
                                © ${new Date().getFullYear()} Notify. All rights reserved.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </body>
        </html>
        `;
    };

    const handleSend = async () => {
        if (!form.to.trim()) {
            Toast.error("Please enter recipient email");
            return;
        }

        if (!form.subject.trim()) {
            Toast.error("Please enter subject");
            return;
        }

        if (ccInput.trim()) {
            addCcEmail(ccInput);
            setCcInput("");
        }

        if (selectedDepartments.length === 0) {
            Toast.error("Please select at least one department");
            return;
        }

        setSending(true);

        try {
            // Sourced from getPricingTableRows() (not selectedDepartments/
            // pricedDepartments directly) so the job_action_logs entry this
            // produces (departmentSummary/new_values.departments in
            // quotations.js's sendQuotationEmail) always reflects exactly
            // what the email actually showed — manually-priced departments
            // as one row each, quoting-fallback departments either rolled up
            // per-department or itemized per-field, depending on the toggle.
            const departmentsPayload = getPricingTableRows().map(row => ({
                department: row.departmentKey,
                label: row.label,
                final_price: row.amount,
                notes: row.notes,
            }));

            await sendQuotationEmail({
                companyId: company.id,
                jobId: job.id,
                shortenedJobId: jobShortId,
                to: form.to.trim(),
                cc: ccEmails,
                subject: form.subject.trim(),
                content: form.content,
                html: buildEmailHtml(),
                contactUrl,
                logoUrl,
                companyName: company?.name || null,
                customerName: job?.customer_name || null,
                departments: departmentsPayload,
                userProfile,
            });

            Toast.success("Quotation email sent successfully", {
                position: "bottom-right",
                autoClose: 2500,
            });

            onSent?.({ jobId: job.id, departments: selectedDepartmentKeys });
            onClose?.();
        } catch (err) {
            console.error("Error sending quotation email:", err);
            Toast.error(err.message || "Failed to send quotation email", {
                position: "bottom-right",
                autoClose: 4000,
            });
        } finally {
            setSending(false);
        }
    };

    const modal = (
        <div className="mobile-email-modal fixed inset-0 z-[9999] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5">
            <div className="bg-white w-full sm:max-w-6xl h-[100dvh] sm:h-[88vh] rounded-none sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col">

                <div className="px-4 sm:px-7 py-4 sm:py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-gray-900 text-white flex items-center justify-center shadow-sm flex-shrink-0">
                                <FiMail className="w-5 h-5" />
                            </div>

                            <div className="min-w-0">
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                                    Send Quotation{" "}
                                    <span className="px-2 py-0.5 ml-1 bg-zinc-200 rounded-lg text-sm">
                                        {job?.unique_id}
                                    </span>
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                    Compose an email and send final pricing to the customer.
                                </p>

                                <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-3">
                                    <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200 text-xs font-medium text-gray-600">
                                        {pricedDepartments.length} Priced Department{pricedDepartments.length !== 1 ? "s" : ""}
                                    </span>
                                    {isJobCancelled && (
                                        <span className="px-2.5 py-1 rounded-full bg-red-50 border border-red-200 text-xs font-medium text-red-600">
                                            Job cancelled
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => !sending && onClose?.()}
                            disabled={sending}
                            className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="mobile-email-body flex-1 min-h-0 p-3 sm:p-6 bg-gray-50 overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-3 sm:gap-6 h-full min-h-0">

                        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col min-h-0">
                            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0">
                                <h4 className="text-sm font-bold text-gray-900">Email Content</h4>
                                <p className="text-xs text-gray-500 mt-1">
                                    Recipient, CC, subject and message.
                                </p>
                            </div>

                            <div className="p-4 sm:p-5 space-y-4 overflow-y-auto min-h-0">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1.5">
                                        Send to
                                    </label>
                                    <input
                                        type="email"
                                        value={form.to}
                                        onChange={e => setForm(prev => ({ ...prev, to: e.target.value }))}
                                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-3 text-sm outline-none focus:bg-white focus:border-gray-900 focus:ring-4 focus:ring-gray-900/5 transition-all"
                                        placeholder="customer@email.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1.5">
                                        CC
                                    </label>

                                    <div className="min-h-[48px] w-full border border-gray-200 bg-gray-50 rounded-xl px-2.5 py-2 flex items-center gap-2 flex-wrap focus-within:bg-white focus-within:border-gray-900 focus-within:ring-4 focus-within:ring-gray-900/5 transition-all">
                                        {ccEmails.map(email => (
                                            <span
                                                key={email}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-medium text-gray-700 shadow-sm max-w-full"
                                            >
                                                <span className="truncate max-w-[190px]">{email}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeCcEmail(email)}
                                                    className="text-gray-400 hover:text-red-500"
                                                >
                                                    <FiX className="w-3.5 h-3.5" />
                                                </button>
                                            </span>
                                        ))}

                                        <input
                                            value={ccInput}
                                            onChange={e => setCcInput(e.target.value)}
                                            onKeyDown={handleCcKeyDown}
                                            onBlur={() => {
                                                if (ccInput.trim()) {
                                                    addCcEmail(ccInput);
                                                    setCcInput("");
                                                }
                                            }}
                                            className="flex-1 min-w-[140px] bg-transparent outline-none text-sm py-1.5"
                                            placeholder={ccEmails.length ? "" : "Add CC emails..."}
                                        />

                                        <button
                                            type="button"
                                            onMouseDown={e => e.preventDefault()}
                                            onClick={() => setUserPickerOpen(true)}
                                            className="ml-auto px-2.5 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-black flex items-center gap-1.5"
                                        >
                                            <FiUsers className="w-3.5 h-3.5" />
                                            Select Users
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1.5">
                                        Subject
                                    </label>
                                    <input
                                        value={form.subject}
                                        onChange={e => setForm(prev => ({ ...prev, subject: e.target.value }))}
                                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-3 text-sm outline-none focus:bg-white focus:border-gray-900 focus:ring-4 focus:ring-gray-900/5 transition-all"
                                    />
                                </div>

                                <div className="relative">
                                    <label className="block text-xs font-bold text-gray-600 mb-1.5">
                                        Message
                                    </label>
                                    <textarea
                                        value={form.content}
                                        onChange={e => setForm(prev => ({ ...prev, content: e.target.value }))}
                                        rows={8}
                                        className="w-full min-h-[190px] sm:min-h-[240px] border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-3 text-sm outline-none focus:bg-white focus:border-gray-900 focus:ring-4 focus:ring-gray-900/5 transition-all resize-none leading-6"
                                    />
                                    <div className="pointer-events-none absolute bottom-3 right-3 max-w-[240px] rounded-xl border border-blue-100 bg-blue-50/95 px-3 py-2 shadow-sm backdrop-blur-sm">
                                        <div className="flex items-center gap-2">
                                            <FiCheck className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                            <p className="text-[11px] font-semibold text-blue-900 leading-snug">
                                                Signature is added automatically
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <label className="block text-xs font-bold text-gray-600">
                                            Pricing table (included in email)
                                        </label>
                                        <span className="text-[11px] text-gray-400">
                                            {selectedDepartments.length} department{selectedDepartments.length !== 1 ? "s" : ""}
                                        </span>
                                    </div>

                                    {selectedDepartments.some(department => department.source !== "final") && (
                                        <div className="flex items-center gap-1 mb-2 p-1 bg-gray-100 rounded-lg w-fit">
                                            <button
                                                type="button"
                                                onClick={() => setLineItemDisplay("itemized")}
                                                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${lineItemDisplay === "itemized"
                                                    ? "bg-white text-gray-900 shadow-sm"
                                                    : "text-gray-500 hover:text-gray-700"
                                                    }`}
                                            >
                                                Itemized
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setLineItemDisplay("rolled_up")}
                                                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${lineItemDisplay === "rolled_up"
                                                    ? "bg-white text-gray-900 shadow-sm"
                                                    : "text-gray-500 hover:text-gray-700"
                                                    }`}
                                            >
                                                Rolled up per department
                                            </button>
                                        </div>
                                    )}

                                    {selectedDepartments.length === 0 ? (
                                        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3.5 py-4 text-xs text-gray-400 text-center">
                                            No departments selected — the pricing table will not be included in the email.
                                        </div>
                                    ) : (
                                        <div className="overflow-hidden rounded-xl border border-gray-200">
                                            <table className="w-full text-xs">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase tracking-wide">{getPricingTableHeaderLabel() || "Item"}</th>
                                                        <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase tracking-wide">Final Price</th>
                                                        <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase tracking-wide">Notes</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {getPricingTableRows().map(row => {
                                                        const DepartmentIcon = row.departmentKey ? DEPARTMENT_ICONS[row.departmentKey] : null;

                                                        return (
                                                            <tr key={row.key}>
                                                                <td className="px-3 py-2 font-semibold text-gray-800 whitespace-nowrap align-top">
                                                                    <div className="flex items-center gap-1.5 pt-1.5">
                                                                        {DepartmentIcon && <DepartmentIcon className="w-3.5 h-3.5 text-gray-400" />}
                                                                        {row.label}
                                                                    </div>
                                                                </td>
                                                                <td className="px-3 py-2 text-gray-800 font-medium font-mono tabular-nums whitespace-nowrap align-top">
                                                                    <div className="pt-1.5">
                                                                        {formatMoney(row.amount)}
                                                                    </div>
                                                                </td>
                                                                <td className="px-3 py-2">
                                                                    {row.editable ? (
                                                                        <input
                                                                            value={row.notes}
                                                                            onChange={e => setDepartmentNotes(row.departmentKey, e.target.value)}
                                                                            placeholder="No notes"
                                                                            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:bg-white focus:border-gray-900 focus:ring-2 focus:ring-gray-900/5 transition-all"
                                                                        />
                                                                    ) : (
                                                                        <span className="block px-2.5 py-1.5 text-xs text-gray-400">
                                                                            {row.notes || "-"}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col min-h-0">
                            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0">
                                <h4 className="text-sm font-bold text-gray-900">Priced Departments</h4>
                                <p className="text-xs text-gray-500 mt-1">
                                    {sentDepartmentKeys.size > 0 &&
                                        `${sentDepartmentKeys.size} previously sent • `
                                    }
                                    Select which department prices should be included in the email.
                                </p>
                            </div>

                            <div className="flex items-center gap-2 ml-auto mr-4 mt-2">
                                <button
                                    type="button"
                                    onClick={selectAllDepartments}
                                    disabled={allDepartmentsSelected}
                                    className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-black disabled:opacity-40"
                                >
                                    Select all
                                </button>

                                <button
                                    type="button"
                                    onClick={unselectSentDepartments}
                                    disabled={sentDepartmentKeys.size === 0}
                                    className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 disabled:opacity-40"
                                >
                                    Unselect Sent
                                </button>

                                <button
                                    type="button"
                                    onClick={clearAllDepartments}
                                    disabled={selectedDepartmentKeys.length === 0}
                                    className="px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 text-xs font-semibold hover:bg-gray-50 disabled:opacity-40"
                                >
                                    Clear all
                                </button>
                            </div>

                            <div className="p-3 sm:p-4 flex-1 overflow-y-auto bg-gray-50/60 min-h-0">
                                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="w-12 px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide"></th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Department</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Final Price</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {pricedDepartments.map(department => {
                                                const checked = selectedDepartmentKeys.includes(department.key);
                                                const alreadySent = sentDepartmentKeys.has(department.key);
                                                const DepartmentIcon = DEPARTMENT_ICONS[department.key];

                                                return (
                                                    <tr
                                                        key={department.key}
                                                        onClick={() => toggleDepartment(department.key)}
                                                        className={`cursor-pointer transition ${checked ? "bg-blue-50/60" : "hover:bg-gray-50"}`}
                                                    >
                                                        <td className="px-4 py-3">
                                                            <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${checked
                                                                ? "bg-blue-600 border-blue-600"
                                                                : "bg-white border-gray-300"
                                                                }`}>
                                                                {checked && <FiCheck className="w-3.5 h-3.5 text-white" />}
                                                            </div>
                                                        </td>

                                                        <td className="px-4 py-3 font-semibold text-gray-800">
                                                            <div className="flex items-center gap-2">
                                                                <DepartmentIcon className="w-3.5 h-3.5 text-gray-400" />
                                                                {department.label}
                                                            </div>
                                                            {alreadySent && (
                                                                <span className="mt-1 block text-[11px] font-bold text-emerald-600">
                                                                    Sent already
                                                                </span>
                                                            )}
                                                        </td>

                                                        <td className="px-4 py-3 text-gray-800 font-medium font-mono tabular-nums whitespace-nowrap">
                                                            {formatMoney(department.pricing.final_price)}
                                                        </td>

                                                        <td className="px-4 py-3 text-gray-500 text-xs max-w-[160px] truncate" title={department.pricing.notes || ""}>
                                                            {department.pricing.notes || "-"}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="px-4 sm:px-5 py-3 border-t border-gray-100 bg-white flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                                {selectedDepartments.length} of {pricedDepartments.length} department{pricedDepartments.length !== 1 ? "s" : ""} selected
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-4 sm:px-7 py-3 sm:py-4 border-t border-gray-100 bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0">
                    <p className="text-xs text-gray-500 hidden sm:block">
                        Quotation emails are sent securely through Notify.
                    </p>

                    <div className="flex items-center justify-end gap-2 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={() => onClose?.()}
                            disabled={sending}
                            className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            onClick={() => setPreviewOpen(true)}
                            className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center justify-center gap-2"
                        >
                            <FiEye className="w-4 h-4" />
                            Preview
                        </button>

                        <button
                            type="button"
                            onClick={handleSend}
                            disabled={sending || selectedDepartments.length <= 0}
                            className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
                        >
                            {sending ? (
                                <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                                <FiSend className="w-4 h-4" />
                            )}
                            {sending ? "Sending..." : "Send Quotation"}
                        </button>
                    </div>
                </div>
            </div>

            {previewOpen && createPortal(
                <div className="mobile-email-modal fixed inset-0 z-[10001] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5">
                    <div className="bg-white w-full sm:max-w-3xl h-[90dvh] sm:h-[82vh] rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Email Preview</h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    This is how the email body will look.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => setPreviewOpen(false)}
                                className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 text-xs text-gray-600 space-y-1">
                            <p><strong>To:</strong> {form.to || "Not specified"}</p>
                            {ccEmails.length > 0 && <p><strong>CC:</strong> {ccEmails.join(", ")}</p>}
                            <p><strong>Subject:</strong> {form.subject || "Not specified"}</p>
                        </div>

                        <div className="flex-1 overflow-y-auto bg-gray-100 p-4">
                            <div
                                className="mx-auto max-w-[680px]"
                                dangerouslySetInnerHTML={{ __html: buildEmailHtml() }}
                            />
                        </div>

                        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setPreviewOpen(false)}
                                className="px-5 py-2.5 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-black"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {userPickerOpen && createPortal(
                <div className="mobile-email-modal fixed inset-0 z-[10000] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5">
                    <div className="bg-white w-full sm:max-w-xl h-[80dvh] sm:h-[620px] rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Select Users</h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    Selected users will be added to CC.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => setUserPickerOpen(false)}
                                className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                                <FiSearch className="w-4 h-4 text-gray-400" />
                                <input
                                    value={userSearch}
                                    onChange={e => setUserSearch(e.target.value)}
                                    placeholder="Search users..."
                                    className="bg-transparent outline-none flex-1 text-sm"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-2">
                            {filteredUsers.map(user => {
                                const checked = pickerSelectedIds.includes(user.id);
                                const name = user.full_name || user.username || user.email;

                                return (
                                    <button
                                        key={user.id}
                                        type="button"
                                        onClick={() => toggleUserPicker(user.id)}
                                        className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-all ${checked
                                            ? "bg-blue-50 border-blue-200"
                                            : "bg-white border-gray-200 hover:border-gray-300"
                                            }`}
                                    >
                                        {user.avatar_url ? (
                                            <img
                                                src={user.avatar_url}
                                                alt={name}
                                                className="w-10 h-10 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center font-bold text-sm">
                                                {(name || "?")[0].toUpperCase()}
                                            </div>
                                        )}

                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-gray-800 truncate">
                                                {name}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {user.email}
                                            </p>
                                        </div>

                                        <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${checked
                                            ? "bg-blue-600 border-blue-600"
                                            : "bg-white border-gray-300"
                                            }`}>
                                            {checked && <FiCheck className="w-3.5 h-3.5 text-white" />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setUserPickerOpen(false)}
                                className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={applySelectedUsersToCc}
                                className="px-5 py-2.5 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-black"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );

    return createPortal(modal, document.body);
};

export default QuotationEmailSender;
